import { useState, useEffect } from 'react';
import { EditorHeader } from './EditorHeader';
import { FeatureTree } from './FeatureTree/index';
import { FeatureProperties } from './FeatureProperties/index';
import { ValueProperties } from './ValueProperties.jsx';

export function FamilyEditor({
    selectedFamily,
    featureSearchQuery,
    setFeatureSearchQuery,
    expandedFolders,
    setExpandedFolders,
    expandedFeatures,
    setExpandedFeatures,
    selectedFeatureId,
    setSelectedFeatureId,
    selectedValueId,
    setSelectedValueId,
    isEditingName,
    setIsEditingName,
    editFamilyName,
    setEditFamilyName,
    savingFamilyName,
    isEditingFeatureName,
    setIsEditingFeatureName,
    editFeatureNameText,
    setEditFeatureNameText,
    isSavingFeatureName,
    isEditingValueName,
    setIsEditingValueName,
    editValueNameText,
    setEditValueNameText,
    isSavingValueName,
    isAddingFeatureInline,
    newFeatureInlineName,
    setNewFeatureInlineName,
    inlineNewValTexts,
    setInlineNewValTexts,
    isSystem,
    setIsSystem,
    isOutput,
    setIsOutput,
    customAttrs,
    featureValues,
    savingFeature,
    activeFeature,
    selectedValue,
    featuresTree,
    features = [],
    suggestions,
    onRenameFamily,
    onSelectFeature,
    onToggleFolder,
    onToggleFeature,
    onRenameFeatureSubmit,
    onRenameValueSubmit,
    onSaveFeatureProperties,
    onAddFeatureInline,
    onAddValueInline,
    onAddAttrRow,
    onRemoveAttrRow,
    onAttrChange,
    onValueFieldChange,
    onValuePropertyChange,
    onReorderValues,
    onCombinationsGenerated,
    onAddValueAttr,
    onRemoveValueAttr,
    onValueAttrChange,
    onDeleteFeature,
    onDeleteValue,
    onExitEditMode,
    onDeleteFamilyClick
}) {
    // Calculate derived values
    const selectedValueIndex = featureValues.findIndex(val => val.id === selectedValueId);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
            {/* Breadcrumbs Top Bar */}
            <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', flexShrink: 0 }}>
                <button onClick={onExitEditMode} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }} className="breadcrumb-link-hover">
                    Families
                </button>
                <span style={{ color: 'var(--text-secondary)' }}>/</span>
                <button onClick={onExitEditMode} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }} className="breadcrumb-link-hover">
                    {selectedFamily.name}
                </button>
                <span style={{ color: 'var(--text-secondary)' }}>/</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Edit</span>
            </div>

            {/* Main Scrollable editor workspace */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Header with In-Place rename */}
                <EditorHeader
                    selectedFamily={selectedFamily}
                    isEditingName={isEditingName}
                    editFamilyName={editFamilyName}
                    setEditFamilyName={setEditFamilyName}
                    setIsEditingName={setIsEditingName}
                    savingFamilyName={savingFamilyName}
                    onRenameFamily={onRenameFamily}
                    onDeleteFamilyClick={onDeleteFamilyClick}
                />

                {/* Scoped Feature Configurations Section */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', minHeight: '500px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                        <div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Feature management</h3>
                            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.15rem 0 0 0' }}>Manage family features</p>
                        </div>
                    </div>

                    {/* Editor Split Workspace */}
                    <div style={{ display: 'flex', gap: '0.75rem', flex: 1, minHeight: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-card)', padding: '1rem' }}>
                        {/* Sub-Left Column: Scoped features hierarchy TREE View */}
                        <FeatureTree
                            featuresTree={featuresTree}
                            featureSearchQuery={featureSearchQuery}
                            setFeatureSearchQuery={setFeatureSearchQuery}
                            expandedFolders={expandedFolders}
                            expandedFeatures={expandedFeatures}
                            selectedFeatureId={selectedFeatureId}
                            selectedValueId={selectedValueId}
                            newFeatureInlineName={newFeatureInlineName}
                            setNewFeatureInlineName={setNewFeatureInlineName}
                            inlineNewValTexts={inlineNewValTexts}
                            setInlineNewValTexts={setInlineNewValTexts}
                            isAddingFeatureInline={isAddingFeatureInline}
                            onToggleFolder={onToggleFolder}
                            onToggleFeature={onToggleFeature}
                            onSelectFeature={onSelectFeature}
                            onAddFeatureInline={onAddFeatureInline}
                            onAddValueInline={onAddValueInline}
                            onReorderValues={onReorderValues}
                        />

                        {/* Sub-Middle Column: Selected feature properties form */}
                        {activeFeature ? (
                            <form onSubmit={onSaveFeatureProperties} style={{ flex: 1, display: 'flex', gap: '0.75rem', overflow: 'hidden' }}>
                                <FeatureProperties
                                    activeFeature={activeFeature}
                                    isEditingFeatureName={isEditingFeatureName}
                                    editFeatureNameText={editFeatureNameText}
                                    setEditFeatureNameText={setEditFeatureNameText}
                                    setIsEditingFeatureName={setIsEditingFeatureName}
                                    isSavingFeatureName={isSavingFeatureName}
                                    isSystem={isSystem}
                                    setIsSystem={setIsSystem}
                                    isOutput={isOutput}
                                    setIsOutput={setIsOutput}
                                    customAttrs={customAttrs}
                                    familyId={selectedFamily?.id}
                                    features={features}
                                    savingFeature={savingFeature}
                                    suggestions={suggestions}
                                    onRenameFeatureSubmit={onRenameFeatureSubmit}
                                    onAddAttrRow={onAddAttrRow}
                                    onRemoveAttrRow={onRemoveAttrRow}
                                    onAttrChange={onAttrChange}
                                    onDeleteFeature={onDeleteFeature}
                                    onClearSelection={() => setSelectedFeatureId(null)}
                                    onCombinationsGenerated={onCombinationsGenerated}
                                />

                                {/* Sub-Right Column: Selected Value Properties */}
                                <ValueProperties
                                    selectedValue={selectedValue}
                                    selectedValueIndex={selectedValueIndex}
                                    isEditingValueName={isEditingValueName}
                                    editValueNameText={editValueNameText}
                                    setEditValueNameText={setEditValueNameText}
                                    setIsEditingValueName={setIsEditingValueName}
                                    isSavingValueName={isSavingValueName}
                                    featureValues={featureValues}
                                    onRenameValueSubmit={onRenameValueSubmit}
                                    onValueFieldChange={onValueFieldChange}
                                    onValuePropertyChange={onValuePropertyChange}
                                    onAddValueAttr={onAddValueAttr}
                                    onRemoveValueAttr={onRemoveValueAttr}
                                    onValueAttrChange={onValueAttrChange}
                                    onDeleteValue={onDeleteValue}
                                />
                            </form>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem', textAlign: 'center', gap: '0.5rem' }}>
                                <i className="fa-solid fa-hand-pointer" style={{ opacity: 0.3, fontSize: '2rem' }}></i>
                                <span>Select a feature or value from the left list to customize it.</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
